import React, { Component } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Row, Col, Alert } from 'reactstrap'
import Select from 'react-select'
import { graphql } from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import withApolloClient from "../withApolloClient"
import moment from 'moment'
import "./MoveDamagesModal.css"

const CarConditionQuery = gql`query AllCarConditions($carId: ID){
  allCarConditions(carId:$carId, orderBy:["-id"]){ 
    edges {
      node {
        id
        pk
        dateAdded
        formData
        cancellationReason
        incidentSet {
          edges {
            node {
              id
              pk
              dateAdded
            }
          }
        }
      }
    }
  }
}`

const BulkMoveIncidentDamagesMutation = gql`mutation BulkMoveIncidentDamagesMutation($input: BulkMoveIncidentDamagesInput!){
    bulkMoveIncidentDamages(input:$input){
        ok
        errors{
            messages
        }
    }
}`

class MoveDamagesModal extends Component {
    constructor(props) {
        super(props)
        this.state = {
            selectedCarCondition: null,
            selectedIncident: null,
            error: '',
            loading: false,
            successMessage: '',
        }
    }

    componentDidMount() {
        const { inspectionId, allCarConditions } = this.props
        if (inspectionId) {
            const option = this.getCarInspectionOptionFromId(inspectionId, allCarConditions)
            if (option) {
                this.setState({ selectedCarCondition: option })
            }
        }
    }

    componentDidUpdate(prevProps) {
        const { inspectionId, allCarConditions } = this.props
        if (
            inspectionId &&
            (inspectionId !== prevProps.inspectionId || allCarConditions !== prevProps.allCarConditions)
        ) {
            const option = this.getCarInspectionOptionFromId(inspectionId, allCarConditions)
            if (option && (!this.state.selectedCarCondition || this.state.selectedCarCondition.value !== option.value)) {
                this.setState({ selectedCarCondition: option })
            }
        }
    }

    handleCarInspectionChange = (option) => {
        this.setState({ selectedCarCondition: option, selectedIncident: null })
    };

    handleIncidentChange = (option) => {
        this.setState({ selectedIncident: option })
    };

    pObj = (obj) => {
        try {
            return typeof obj === 'string' ? JSON.parse(obj) : obj
        } catch (e) {
            return obj
        }
    }

    handleMoveDamages = () => {
        const { selectedIncident } = this.state
        if (!selectedIncident) {
            this.setState({ error: 'Please select an incident to move the damages.' })
            return
        }
        this.setState({ loading: true, error: '', successMessage: '' })
        const input = {
            incidentDamageIds: this.props.damageIds,
            newIncidentId: selectedIncident.value,
        }
        this.props.apolloClient.mutate({
            mutation: BulkMoveIncidentDamagesMutation,
            variables: { input },
        }).then((result) => {
            if (result && result.data && result.data.bulkMoveIncidentDamages && result.data.bulkMoveIncidentDamages.ok) {
                this.setState({ successMessage: `Successfully moved ${this.props.damageIds.length} damage(s).`, loading: false })
                this.props.refetchQuery && this.props.refetchQuery()
                setTimeout(() => {
                    this.props.handleClose()
                }, 1000)
            } else {
                let error = 'An error occurred, could not complete request.'
                if (result.data && result.data.bulkMoveIncidentDamages && result.data.bulkMoveIncidentDamages.errors && result.data.bulkMoveIncidentDamages.errors[0] && result.data.bulkMoveIncidentDamages.errors[0].messages) {
                    error = result.data.bulkMoveIncidentDamages.errors[0].messages.toString()
                }
                this.setState({ error, loading: false })
            }
        }).catch(() => {
            this.setState({ error: 'An error has occurred', loading: false })
        })
    };

    buildCarInspectionOption = (edge) => {
        const formType = edge.node.formData && this.pObj(edge.node.formData) && "form_type" in this.pObj(edge.node.formData)
            ? this.pObj(edge.node.formData)["form_type"].charAt(0).toUpperCase() + this.pObj(edge.node.formData)["form_type"].slice(1).toLowerCase()
            : "---"
        const date = edge.node.dateAdded ? moment(edge.node.dateAdded).tz("America/New_York").format('MMM DD, YYYY h:mm A') : "--"
        return {
            value: edge.node.id,
            label: `${formType} - ${date}${edge.node.cancellationReason ? " (Canceled)" : ""}`
        }
    }

    getCarInspectionOptionFromId = (inspectionId, allCarConditions) => {
        if (!inspectionId || !allCarConditions || !allCarConditions.edges) return null
        const edge = allCarConditions.edges.find(edge => edge.node.id === inspectionId)
        return edge ? this.buildCarInspectionOption(edge) : null
    }

    buildIncidentOption = (edge) => ({
        value: edge.node.id,
        label: `Incident #${edge.node.pk} - ${edge.node.dateAdded ? moment(edge.node.dateAdded).tz("America/New_York").format('MMM DD, YYYY h:mm A') : '--'}`
    });

    render() {
        const { isOpen, handleClose, allCarConditions, inspectionId, loading: carConditionsLoading } = this.props
        const { selectedCarCondition, selectedIncident, error, loading, successMessage } = this.state

        const carInspectionOptions = allCarConditions && allCarConditions.edges
            ? allCarConditions.edges.map(this.buildCarInspectionOption)
            : []

        let incidentOptions = []
        if (selectedCarCondition && allCarConditions && allCarConditions.edges) {
            const selected = allCarConditions.edges.find(edge => edge.node.id === selectedCarCondition.value)
            if (selected && selected.node && selected.node.incidentSet && selected.node.incidentSet.edges && Array.isArray(selected.node.incidentSet.edges)) {
                incidentOptions = selected.node.incidentSet.edges.map(this.buildIncidentOption)
            }
        }

        return (
            <Modal isOpen={isOpen} toggle={handleClose} className="move-damages-modal">
                <ModalHeader>Move <strong>{this.props.damageIds?.length || 0}</strong> Selected Damage</ModalHeader>
                <ModalBody>
                    {error && <Alert color="danger">{error}</Alert>}
                    {successMessage && <Alert color="success">{successMessage}</Alert>}
                    <Row>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>Select Car Inspection</Label>
                                <Select
                                    options={carInspectionOptions}
                                    value={selectedCarCondition}
                                    onChange={this.handleCarInspectionChange}
                                    placeholder="Select Car Inspection"
                                    isLoading={carConditionsLoading}
                                />
                            </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>Select Incident</Label>
                                <Select
                                    options={incidentOptions}
                                    value={selectedIncident}
                                    onChange={this.handleIncidentChange}
                                    placeholder="Select Incident"
                                    isDisabled={!selectedCarCondition}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button className="move-damages-modal-secondary-btn" onClick={handleClose} disabled={loading}>Cancel</Button>
                    <Button className="move-damages-modal-primary-btn" onClick={this.handleMoveDamages} disabled={loading || !selectedIncident}>{loading ? 'Moving...' : `Move Damages`}</Button>
                </ModalFooter>
            </Modal>
        )
    }
}

export default graphql(CarConditionQuery, {
    options: (props) => ({
        variables: { carId: props.carId },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
    }),
    props: ({ data: { loading, allCarConditions } }) => ({
        loading,
        allCarConditions
    })
})(withApolloClient(MoveDamagesModal)) 